import { omit } from 'ramda';
import { SiteAvailabilityControl } from 'src/net-availability/enums/site-availability-control';
import {
  AssetDeclaredAvailability,
  AssetLimitForNetAvailability,
  DeclaredPowerAndEnergyRating,
  IPowerAndEnergyRating,
  SiteDeclaredAvailability,
  SiteLimitForNetAvailability,
} from 'src/net-availability/types';
import { CheckAssetNamesAndAssetLimitsAndAssetDeclaredAvailabilitiesError } from 'src/net-availability/utils/asset-limit';
import { AssetNamesError } from 'src/net-availability/utils/asset-names';
import {
  UndeclaredMaxEnergyCapacityError,
  UndeclaredMaxExportPowerError,
  UndeclaredMaxImportPowerError,
} from 'src/net-availability/utils/power-and-energy-rating';
import { CheckAssetNamesAndSiteLimitsError } from 'src/net-availability/utils/site-limit';
import { BaseError } from 'src/types/base-error';
import { DateAsNumber } from 'src/types/date';
import { getMapGroupFrom } from 'src/utils/map';
import {
  DateInterval,
  getGroupedByContinuousDateIntervals,
  getTimeWindowFromInterval,
} from 'src/utils/time-interval';

export interface INetAvailabilityError extends BaseError {
  kind:
    | 'AssetNamesAndSiteLimitsError'
    | 'AssetNamesAndAssetLimitsAndAssetDeclaredAvailabilitiesError'
    | 'SiteLevelUndeclaredRatingPropertyError'
    | 'AssetLevelUndeclaredRatingPropertyError'
    | 'AssetLevelUndeclaredRatingPropertiesError'
    | 'SiteLevelUniformPerAssetDisaggregationError';
}

export class AssetNamesAndSiteLimitsError
  extends BaseError
  implements INetAvailabilityError
{
  kind = 'AssetNamesAndSiteLimitsError' as const;

  constructor(
    cause: CheckAssetNamesAndSiteLimitsError,
    context: {
      start: Date;
      end: Date;
      timeHorizon: DateInterval;
      siteLimitsForInterval: SiteLimitForNetAvailability[];
      rawSiteLimits: SiteLimitForNetAvailability[];
    },
  ) {
    super(
      `Asset names / site limits error for interval ${context.start} - ${context.end}`,
      {
        cause,
        context: JSON.stringify(context),
      },
    );
  }
}

export class AssetNamesAndAssetLimitsAndAssetDeclaredAvailabilitiesError
  extends BaseError
  implements INetAvailabilityError
{
  kind = 'AssetNamesAndAssetLimitsAndAssetDeclaredAvailabilitiesError' as const;

  constructor(
    cause: CheckAssetNamesAndAssetLimitsAndAssetDeclaredAvailabilitiesError,
    context: {
      start: Date;
      end: Date;
      timeHorizon: DateInterval;
      assetNames: string[];
      assetLimitsForInterval: AssetLimitForNetAvailability[];
      rawAssetLimits: AssetLimitForNetAvailability[];
    },
  ) {
    super(
      `Asset name / asset limits / asset declared availabilities error for interval ${context.start} - ${context.end}`,
      {
        cause,
        context: JSON.stringify(context),
      },
    );
  }
}

export class SiteLevelUndeclaredRatingPropertyError
  extends BaseError
  implements INetAvailabilityError
{
  kind = 'SiteLevelUndeclaredRatingPropertyError' as const;

  constructor(
    cause:
      | UndeclaredMaxImportPowerError
      | UndeclaredMaxExportPowerError
      | UndeclaredMaxEnergyCapacityError,
    context: {
      start: Date;
      end: Date;
      timeHorizon: DateInterval;
      siteLimitForInterval: SiteLimitForNetAvailability;
      rawSiteLimits: SiteLimitForNetAvailability[];
      limitForIntervalAsMinDeclaredPowerAndEnergyRating: DeclaredPowerAndEnergyRating;
      activeDeclaredAvailabilitiesForInterval: SiteDeclaredAvailability[];
      rawSiteDeclaredAvailabilities: SiteDeclaredAvailability[];
    },
  ) {
    super(`Undeclared rating property`, {
      cause,
      context: JSON.stringify(context),
    });
  }
}

export class AssetLevelUndeclaredRatingPropertyError
  extends BaseError
  implements INetAvailabilityError
{
  kind = 'AssetLevelUndeclaredRatingPropertyError' as const;

  constructor(
    cause:
      | UndeclaredMaxImportPowerError
      | UndeclaredMaxExportPowerError
      | UndeclaredMaxEnergyCapacityError,
    public details: {
      start: Date;
      end: Date;
      timeHorizon: DateInterval;
      assetLimitsForInterval: AssetLimitForNetAvailability[];
      rawAssetLimits: AssetLimitForNetAvailability[];
      assetName: string;
      limitAsPowerAndEnergyRatingForIntervalForAsset: IPowerAndEnergyRating;
      activeDeclaredAvailabilitiesForIntervalForAsset: AssetDeclaredAvailability[];
      rawAssetDeclaredAvailabilities: AssetDeclaredAvailability[];
    },
  ) {
    super(`Undeclared rating property`, {
      cause,
      context: JSON.stringify(details),
    });
  }
}

export class AssetLevelUndeclaredRatingPropertiesError
  extends BaseError
  implements INetAvailabilityError
{
  kind = 'AssetLevelUndeclaredRatingPropertiesError' as const;

  constructor(details: AssetLevelUndeclaredRatingPropertyError['details'][]) {
    super(`Undeclared rating property`, {
      context: JSON.stringify(details),
    });
  }
}

export class SiteLevelUniformPerAssetDisaggregationError
  extends BaseError
  implements INetAvailabilityError
{
  kind = 'SiteLevelUniformPerAssetDisaggregationError' as const;

  constructor(
    cause: AssetNamesError,
    context: {
      start: Date;
      end: Date;
      timeHorizon: DateInterval;
      assetNames: string[];
    },
  ) {
    super(`Undeclared rating property`, {
      cause,
      context: JSON.stringify(context),
    });
  }
}

export const getLimitWithEnds = <
  TLimit extends SiteLimitForNetAvailability | AssetLimitForNetAvailability,
>(
  limits: TLimit[],
  timeHorizonEnd: Date,
): (TLimit & {
  start: Date;
  end: Date;
})[] =>
  limits
    .sort((a, b) => +a.start - +b.start)
    .map((it, i, array) => {
      const withEnd: TLimit & {
        start: Date;
        end: Date;
      } = {
        ...it,
        start: it.start,
        end: array[i + 1]?.start ?? timeHorizonEnd,
      };

      return withEnd;
    });

type DateIntervalGroupOfLimitsAndDeclaredAvailabilities<
  TLimit extends SiteLimitForNetAvailability | AssetLimitForNetAvailability,
  TDeclaredAvailability extends
    SiteDeclaredAvailability | AssetDeclaredAvailability,
> = DateInterval & {
  limits: TLimit[];
  declaredAvailabilities: TDeclaredAvailability[];
};

export const getLimitsAndDeclaredAvailabilitiesGroupedByDateInterval = <
  TSiteAvailabilityControl extends SiteAvailabilityControl,
  TLimit extends
    (TSiteAvailabilityControl extends SiteAvailabilityControl.SiteLevel
      ? SiteLimitForNetAvailability
      : AssetLimitForNetAvailability),
  TDeclaredAvailability extends
    SiteDeclaredAvailability | AssetDeclaredAvailability,
>({
  siteAvailabilityControl,
  limits,
  declaredAvailabilities,
  timeHorizon,
  additionalTimestamps,
}: {
  siteAvailabilityControl: TSiteAvailabilityControl;
  limits: TLimit[];
  declaredAvailabilities: TDeclaredAvailability[];
  timeHorizon: DateInterval;
  additionalTimestamps?: Parameters<
    typeof getGroupedByContinuousDateIntervals
  >[0]['additionalTimestamps'];
}): DateIntervalGroupOfLimitsAndDeclaredAvailabilities<
  TLimit,
  TDeclaredAvailability
>[] => {
  const limitWithEnds = (() => {
    if (siteAvailabilityControl === SiteAvailabilityControl.SiteLevel) {
      return getLimitWithEnds(limits, timeHorizon.end);
    }

    if (siteAvailabilityControl === SiteAvailabilityControl.AssetLevel) {
      const limitsByAssetName = getMapGroupFrom(
        limits as AssetLimitForNetAvailability[],
      ).byKey('assetName') as Map<
        AssetLimitForNetAvailability['assetName'],
        TLimit[]
      >;

      return Array.from(limitsByAssetName.values()).flatMap((assetLimits) =>
        getLimitWithEnds(assetLimits, timeHorizon.end),
      );
    }

    /* istanbul ignore next */
    return siteAvailabilityControl satisfies never;
  })();

  const allRelevantTimestamps = [
    ...(additionalTimestamps ?? []),
    ...limitWithEnds.flatMap(getTimeWindowFromInterval),
    ...declaredAvailabilities.flatMap(getTimeWindowFromInterval),
  ];

  const omitEnd = omit(['end']);

  const limitTimeWindowGroups = getGroupedByContinuousDateIntervals({
    items: limitWithEnds,
    timeHorizon,
    additionalTimestamps: allRelevantTimestamps,
  }).map(({ start, end, items }) => ({
    start,
    end,
    items: items.map((item) => omitEnd(item)) as TLimit[],
  }));

  const declaredAvailabilityTimeWindowGroups =
    getGroupedByContinuousDateIntervals({
      items: declaredAvailabilities,
      timeHorizon,
      additionalTimestamps: allRelevantTimestamps,
    });

  const dateIntervalGroupsMap: Map<
    `${DateAsNumber}||${DateAsNumber}`,
    DateIntervalGroupOfLimitsAndDeclaredAvailabilities<
      TLimit,
      TDeclaredAvailability
    >
  > = new Map();

  const getDateIntervalGroupForIntervalOrSetNewEmpty = ({
    start,
    end,
  }: DateInterval) => {
    const key = `${+start}||${+end}` as const;

    return (
      dateIntervalGroupsMap.get(key) ??
      dateIntervalGroupsMap
        .set(key, {
          start,
          end,
          limits: [],
          declaredAvailabilities: [],
        })
        .get(key)!
    );
  };

  for (const it of limitTimeWindowGroups) {
    getDateIntervalGroupForIntervalOrSetNewEmpty(it).limits.push(...it.items);
  }

  for (const it of declaredAvailabilityTimeWindowGroups) {
    getDateIntervalGroupForIntervalOrSetNewEmpty(
      it,
    ).declaredAvailabilities.push(...it.items);
  }

  return Array.from(dateIntervalGroupsMap.values());
};
